<template>
  <div :class="ns.b()">
    <slot
      v-if="$slots.default"
      :result="innerResult"
      :set-field="setField"
      :update-result="patchResult"
      :disabled="disabled"
    />
    <z-form
      v-else-if="mode === 'result' && ruleResult"
      :model="innerResult"
      :label-width="labelWidth"
      :disabled="disabled"
    >
      <z-form-item
        :label="ruleResult.label"
        :required="true"
        :class="ns.e('item')"
      >
        <rule-value
          :model-value="innerResult[valueKey]"
          :type="ruleResult.type"
          :data-source="ruleResult.dataSource"
          :disabled="disabled"
          @update:model-value="(v) => setField(valueKey, v)"
        />
      </z-form-item>
    </z-form>
    <z-form
      v-else-if="mode === 'error'"
      :model="innerResult"
      :label-width="labelWidth"
      :disabled="disabled"
    >
      <z-form-item
        :label="t('z.rules.errorMessage')"
        :required="true"
        :class="ns.e('item')"
      >
        <z-input
          :model-value="String(innerResult[messageKey] ?? '')"
          type="textarea"
          :rows="2"
          :disabled="disabled"
          :placeholder="t('z.rules.errorMessagePlaceholder')"
          @update:model-value="(v) => setField(messageKey, v)"
        />
      </z-form-item>
    </z-form>
    <div v-else :class="ns.e('empty')">{{ t('z.rules.resultEmpty') }}</div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue'
import ZForm, { ZFormItem } from '@zoryn/components/form'
import ZInput from '@zoryn/components/input'
import { useLocale, useNamespace } from '@zoryn/hooks'
import RuleValue from './rule-value.vue'
import {
  RULE_ERROR_MESSAGE_KEY,
  RULE_RESULT_VALUE_KEY,
  type RuleResultSchema,
  type RuleResultType,
  type RuleResultValue,
} from './rules'

defineOptions({
  name: 'ZRuleResult',
})

const props = withDefaults(
  defineProps<{
    modelValue?: RuleResultValue
    mode?: Extract<RuleResultType, 'result' | 'error'>
    ruleResult?: RuleResultSchema
    disabled?: boolean
    labelWidth?: string
  }>(),
  {
    modelValue: () => ({}),
    mode: 'result',
    disabled: false,
    labelWidth: '96px',
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: RuleResultValue]
  change: [value: RuleResultValue]
}>()

const ns = useNamespace('rule-result')
const { t } = useLocale()

const valueKey = RULE_RESULT_VALUE_KEY
const messageKey = RULE_ERROR_MESSAGE_KEY
const innerResult = reactive<RuleResultValue>({})

function defaultByType(type: RuleResultSchema['type']) {
  if (type === 'switch') return false
  if (type === 'number') return undefined
  return null
}

function mergeDefaults(value: RuleResultValue): RuleResultValue {
  const next: RuleResultValue = { ...value }

  if (props.mode === 'error') {
    if (next[messageKey] === undefined) {
      next[messageKey] = ''
    }
    return next
  }

  if (props.mode === 'result' && props.ruleResult) {
    if (next[valueKey] === undefined) {
      next[valueKey] =
        props.ruleResult.defaultValue !== undefined
          ? props.ruleResult.defaultValue
          : defaultByType(props.ruleResult.type)
    }
  }

  return next
}

function syncFromProps() {
  const merged = mergeDefaults(props.modelValue ?? {})
  for (const key of Object.keys(innerResult)) {
    if (!(key in merged)) {
      delete innerResult[key]
    }
  }
  Object.assign(innerResult, merged)
}

watch(
  () => [props.modelValue, props.mode, props.ruleResult] as const,
  () => syncFromProps(),
  { immediate: true, deep: true }
)

function emitResult() {
  const next = { ...innerResult }
  emit('update:modelValue', next)
  emit('change', next)
}

function setField(field: string, value: unknown) {
  innerResult[field] = value
  emitResult()
}

function patchResult(value: RuleResultValue) {
  Object.assign(innerResult, value)
  emitResult()
}

defineExpose({
  getResult: () => ({ ...innerResult }),
  reset: () => {
    const next = mergeDefaults({})
    for (const key of Object.keys(innerResult)) {
      delete innerResult[key]
    }
    Object.assign(innerResult, next)
    emitResult()
  },
})
</script>
